import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import MedicationIcon from "@mui/icons-material/Medication";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getDisplayProduct, getPackTypeDisplay, getSealDetailsDisplay } from "../../../components/utils/utils";

interface IShoppingCartItemProps {
  item: any;
  availableItems: any;
  setAvailableItems: any;
}

const SHORTER_DATE_MESSAGE =
  "Expiration cannot be earlier than listed. Mark the item out of stock instead.";

const chipSx = {
  height: 24,
  fontSize: 13,
  fontWeight: 500,
  color: "text.secondary",
  borderColor: "rgba(15, 23, 42, 0.12)",
  "& .MuiChip-label": { px: 0.75 },
} as const;

const sealedChipSx = {
  ...chipSx,
  color: "success.dark",
  borderColor: "success.light",
  bgcolor: "rgba(46, 125, 50, 0.04)",
} as const;

const nonSealedChipSx = {
  ...chipSx,
  color: "#B45309",
  borderColor: "#F59E0B",
  bgcolor: "#FEF3C7",
} as const;

const inputSx = {
  "& .MuiInputBase-root": { fontSize: 13, bgcolor: "#fff" },
  "& .MuiInputBase-input": { py: 0.75 },
  "& .MuiFormLabel-root": { fontSize: 12 },
} as const;

const actionBtnSx = {
  minWidth: "auto",
  px: 1,
  py: 0.25,
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  lineHeight: 1.4,
} as const;

const editBtnSx = {
  minWidth: "auto",
  px: 1,
  py: 0.375,
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  lineHeight: 1.2,
  borderRadius: "6px",
  flexShrink: 0,
  color: "text.primary",
  borderColor: "rgba(15, 23, 42, 0.18)",
  bgcolor: "#fff",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  "& .MuiButton-startIcon": { mr: 0.5, ml: -0.125 },
  "&:hover": {
    bgcolor: "grey.50",
    borderColor: "rgba(15, 23, 42, 0.28)",
  },
} as const;

interface InlinePillProps {
  selected: boolean;
  label: string;
  isAvailable: boolean;
  onClick: () => void;
}

const InlinePill: React.FC<InlinePillProps> = ({ selected, label, isAvailable, onClick }) => (
  <Button
    onClick={onClick}
    disableRipple
    sx={{
      borderRadius: "999px",
      py: 0.25,
      px: 1,
      minHeight: 26,
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.2,
      textTransform: "none",
      bgcolor: selected ? "#fff" : "transparent",
      color: selected
        ? isAvailable
          ? "success.dark"
          : "text.primary"
        : "text.secondary",
      boxShadow: selected ? "0 1px 2px rgba(15, 23, 42, 0.08)" : "none",
      "&:hover": { bgcolor: selected ? "#fff" : "rgba(255, 255, 255, 0.5)" },
    }}
  >
    {label}
  </Button>
);

export const ActionRequiredItem: React.FunctionComponent<IShoppingCartItemProps> = ({
  item,
  availableItems,
  setAvailableItems,
}) => {
  const currentItem = availableItems.find((i: any) => i.id === item.id) ?? item;
  const displayProduct = getDisplayProduct(currentItem);
  const originalExpMonth = dayjs(displayProduct.expDate).startOf("month");
  const originalLotNumber = displayProduct.lotNumber || "";
  const originalExpDate = dayjs(displayProduct.expDate);

  const [confirm, setConfirm] = useState<string>(currentItem.status === 1 ? "AVAILABLE" : "OUT_OF_STOCK");
  const [imageUrl, setImageUrl] = useState<string>("");
  const [editingLot, setEditingLot] = useState<boolean>(false);
  const [editingExp, setEditingExp] = useState<boolean>(false);
  const [lotDraft, setLotDraft] = useState<string>(currentItem.editedLotNumber ?? originalLotNumber);
  const [expDraft, setExpDraft] = useState<Dayjs>(
    currentItem.editedExpDate ? dayjs(currentItem.editedExpDate) : originalExpDate
  );

  const isItemAvailable = confirm === "AVAILABLE";

  const patchItem = (patch: Record<string, unknown>) => {
    const newItems = [...availableItems];
    const foundIndex = newItems.findIndex((newItem: any) => newItem.id === item.id);
    if (foundIndex === -1) return;
    Object.assign(newItems[foundIndex], patch);
    setAvailableItems(newItems);
  };

  const savedLotNumber = currentItem.editedLotNumber ?? originalLotNumber;
  const savedExpDate = currentItem.editedExpDate ? dayjs(currentItem.editedExpDate) : originalExpDate;

  const dateTooEarly = !!expDraft && expDraft.startOf("month").isBefore(originalExpMonth);
  const hasLotEdit = currentItem.editedLotNumber !== undefined && currentItem.editedLotNumber !== originalLotNumber;
  const hasExpEdit =
    currentItem.editedExpDate !== undefined &&
    dayjs(currentItem.editedExpDate).startOf("month").valueOf() !== originalExpMonth.valueOf();

  const setAvailability = (value: "AVAILABLE" | "OUT_OF_STOCK") => {
    const newItems = [...availableItems];
    const foundIndex = newItems.findIndex((newItem: any) => newItem.id === item.id);
    if (foundIndex === -1) return;

    if (value === "OUT_OF_STOCK") {
      newItems[foundIndex].status = 2;
      newItems[foundIndex].editedLotNumber = undefined;
      newItems[foundIndex].editedExpDate = undefined;
      newItems[foundIndex].editInvalid = false;
      setEditingLot(false);
      setEditingExp(false);
      setLotDraft(originalLotNumber);
      setExpDraft(originalExpDate);
    } else {
      newItems[foundIndex].status = 1;
    }

    setAvailableItems(newItems);
    setConfirm(value);
  };

  useEffect(() => {
    if (!item) return;
    const formatedNdc = displayProduct.ndc?.replace(/-/g, "");
    if (!formatedNdc) return;
    const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formatedNdc}`;
    axios.get(imageUrlApi).then((response) => {
      setImageUrl(response.data.imageUrl);
    });
  }, [item]);

  const openExpEdit = () => {
    setExpDraft(savedExpDate);
    setEditingExp(true);
    setEditingLot(false);
  };

  const saveExpEdit = () => {
    if (dateTooEarly) {
      patchItem({ editInvalid: true });
      return;
    }
    const monthChanged = expDraft.startOf("month").valueOf() !== originalExpMonth.valueOf();
    patchItem({
      editedExpDate: monthChanged
        ? expDraft.startOf("month").format("YYYY-MM-DD")
        : undefined,
      editInvalid: false,
    });
    setEditingExp(false);
  };

  const cancelExpEdit = () => {
    setExpDraft(savedExpDate);
    patchItem({ editInvalid: false });
    setEditingExp(false);
  };

  const openLotEdit = () => {
    setLotDraft(savedLotNumber);
    setEditingLot(true);
    setEditingExp(false);
  };

  const saveLotEdit = () => {
    const changed = lotDraft !== originalLotNumber;
    patchItem({
      editedLotNumber: changed ? lotDraft : undefined,
      editInvalid: false,
    });
    setEditingLot(false);
  };

  const cancelLotEdit = () => {
    setLotDraft(savedLotNumber);
    setEditingLot(false);
  };

  const isSealed = displayProduct.packageCondition === 1;
  const packTypeLabel = getPackTypeDisplay(displayProduct.type, displayProduct.packaging);
  const sealDetails = displayProduct.sealDetails
    ? getSealDetailsDisplay(displayProduct.sealDetails)
    : null;

  const editPanelSx = {
    mt: 0.5,
    pl: 1,
    py: 0.75,
    pr: 0.75,
    borderLeft: "2px solid",
    borderColor: "rgba(15, 23, 42, 0.1)",
    bgcolor: "rgba(15, 23, 42, 0.02)",
    borderRadius: "0 6px 6px 0",
  } as const;

  const renderEditButton = (onClick: () => void) => (
    <Button
      size="small"
      variant="outlined"
      onClick={onClick}
      startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
      sx={editBtnSx}
    >
      Edit
    </Button>
  );

  const renderFieldActions = (onSave: () => void, onCancel: () => void, saveDisabled = false) => (
    <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
      <Button
        size="small"
        variant="contained"
        disableElevation
        onClick={onSave}
        disabled={saveDisabled}
        sx={{
          ...actionBtnSx,
          bgcolor: "grey.800",
          color: "#fff",
          "&:hover": { bgcolor: "grey.900" },
          "&.Mui-disabled": { bgcolor: "grey.300", color: "rgba(255, 255, 255, 0.9)" },
        }}
      >
        Save
      </Button>
      <Button size="small" variant="text" onClick={onCancel} sx={{ ...actionBtnSx, color: "text.secondary" }}>
        Cancel
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        px: 1.25,
        py: 0.875,
        mt: 0.875,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            bgcolor: "#fafafa",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {imageUrl ? (
            <Box component="img" src={imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <MedicationIcon sx={{ fontSize: 26, color: "text.disabled" }} />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                lineHeight: 1.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayProduct.drugName}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, flexShrink: 0 }}>${currentItem.totalAmount}</Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.3 }}>
            NDC {displayProduct.ndc}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mt: 0.625 }}>
        <Chip label={`Quantity: ${currentItem.quantity}`} size="small" variant="outlined" sx={chipSx} />
        <Chip label={`Package: ${displayProduct.quantityPerPack}`} size="small" variant="outlined" sx={chipSx} />
        <Chip label={packTypeLabel} size="small" variant="outlined" sx={chipSx} />
        <Tooltip title={sealDetails || (isSealed ? "Sealed" : "Non-Sealed")} arrow placement="top">
          <Chip
            label={isSealed ? "Sealed" : "Non-Sealed"}
            size="small"
            variant="outlined"
            sx={isSealed ? sealedChipSx : nonSealedChipSx}
          />
        </Tooltip>

        <Box
          sx={{
            display: "inline-flex",
            ml: { xs: 0, sm: "auto" },
            mt: { xs: 0.25, sm: 0 },
            p: 0.25,
            bgcolor: "grey.100",
            borderRadius: "999px",
            flexShrink: 0,
          }}
        >
          <InlinePill
            selected={confirm === "AVAILABLE"}
            label="Available"
            isAvailable
            onClick={() => setAvailability("AVAILABLE")}
          />
          <InlinePill
            selected={confirm === "OUT_OF_STOCK"}
            label="Out of stock"
            isAvailable={false}
            onClick={() => setAvailability("OUT_OF_STOCK")}
          />
        </Box>
      </Box>

      <Box
        sx={{
          mt: 0.625,
          pt: 0.625,
          borderTop: "1px solid rgba(15, 23, 42, 0.06)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "stretch",
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, pr: { sm: 1.25 } }}>
            {!editingExp ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, minHeight: 28 }}>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  Expiration{" "}
                  <Box component="span" sx={{ color: "text.primary", fontWeight: 400 }}>
                    {savedExpDate.format("MM/YYYY")}
                  </Box>
                  {hasExpEdit && (
                    <Box component="span" sx={{ fontSize: 11, color: "text.disabled", ml: 0.5 }}>
                      edited
                    </Box>
                  )}
                </Typography>
                {isItemAvailable && renderEditButton(openExpEdit)}
              </Box>
            ) : (
              <Box sx={editPanelSx}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Expiration"
                    value={expDraft}
                    onChange={(date) => setExpDraft(date || originalExpDate)}
                    views={["month", "year"]}
                    format="MM/YYYY"
                    minDate={originalExpMonth}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        error: dateTooEarly,
                        sx: inputSx,
                      },
                    }}
                  />
                </LocalizationProvider>
                {dateTooEarly && (
                  <Typography sx={{ fontSize: 11, color: "error.main", mt: 0.5, lineHeight: 1.35 }}>
                    {SHORTER_DATE_MESSAGE}
                  </Typography>
                )}
                {renderFieldActions(saveExpEdit, cancelExpEdit, dateTooEarly)}
              </Box>
            )}
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              display: { xs: "none", sm: "block" },
              borderColor: "rgba(15, 23, 42, 0.1)",
              mx: 0.5,
            }}
          />
          <Divider
            sx={{
              display: { xs: "block", sm: "none" },
              borderColor: "rgba(15, 23, 42, 0.1)",
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0, pl: { sm: 1.25 } }}>
            {!editingLot ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, minHeight: 28 }}>
                <Typography sx={{ fontSize: 13, color: "text.secondary", minWidth: 0 }}>
                  Lot{" "}
                  <Box component="span" sx={{ color: "text.primary", fontWeight: 400, wordBreak: "break-all" }}>
                    {savedLotNumber}
                  </Box>
                  {hasLotEdit && (
                    <Box component="span" sx={{ fontSize: 11, color: "text.disabled", ml: 0.5 }}>
                      edited
                    </Box>
                  )}
                </Typography>
                {isItemAvailable && renderEditButton(openLotEdit)}
              </Box>
            ) : (
              <Box sx={editPanelSx}>
                <TextField
                  label="Lot number"
                  size="small"
                  fullWidth
                  value={lotDraft}
                  onChange={(e) => setLotDraft(e.target.value)}
                  sx={inputSx}
                />
                {renderFieldActions(saveLotEdit, cancelLotEdit)}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ActionRequiredItem;
